import React, { useEffect, useRef, useState } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import useReveal from "@/hooks/useReveal";
import { getServiceBySlug as staticGet } from "@/data/services";
import { getServiceBySlug as dbGet, ServiceRow } from "@/lib/api";
import "@/styles/pages.css";
import "@/components/Floaters/floaters.css";

interface ResolvedBrand { name: string; logo_url?: string; height?: number; font_size?: number }
interface Resolved {
  name: string; paragraph1: string; paragraph2: string;
  carousel: string[]; featured: string;
  brands: ResolvedBrand[]; isHtml: boolean;
  heroQuote: string;
}

const fromStatic = (slug?: string): Resolved | null => {
  const s = staticGet(slug); if (!s) return null;
  return { name: s.name, paragraph1: s.paragraph1, paragraph2: s.paragraph2, carousel: s.carousel, featured: s.featured, brands: (s.brands || []).map((name: string) => ({ name })), isHtml: false, heroQuote: (slug && DEFAULT_HERO_QUOTES[slug]) || "" };
};
const fromDb = (r: ServiceRow): Resolved => ({
  name: r.name,
  paragraph1: r.description_1 || "",
  paragraph2: r.description_2 || "",
  carousel: r.carousel_images && r.carousel_images.length ? r.carousel_images : [],
  featured: r.featured_image_url || "",
  brands: (r.brands || [])
    .map((b: any) => ({
      name: (b?.name || "").trim(),
      logo_url: (b?.logo_url || "").trim(),
      height: typeof b?.height === "number" ? b.height : undefined,
      font_size: typeof b?.font_size === "number" ? b.font_size : undefined,
    }))
    .filter((b: ResolvedBrand) => !!b.name || !!b.logo_url),
  isHtml: true,
  heroQuote: (r.hero_quote || "").trim() || (r.slug && DEFAULT_HERO_QUOTES[r.slug]) || "",
});

const DEFAULT_SERVICE_HERO_BY_SLUG: Record<string, string> = {
  "audio":              "https://images.unsplash.com/photo-1598488035139-bdbb2231ce04?w=1600&q=85&auto=format&fit=crop",
  "lighting":           "https://images.unsplash.com/photo-1540039155733-5bb30b53aa14?w=1600&q=85&auto=format&fit=crop",
  "led-video":          "https://images.unsplash.com/photo-1492016661000-14a3b3034a70?w=1600&q=85&auto=format&fit=crop",
  "projection-mapping": "https://images.unsplash.com/photo-1574717024653-61fd2cf4d44d?w=1600&q=85&auto=format&fit=crop",
  "sfx-laser":          "https://images.unsplash.com/photo-1429962714451-bb934ecdc4ec?w=1600&q=85&auto=format&fit=crop",
  "rigging-staging":    "https://images.unsplash.com/photo-1516450360452-9312f5e86fc7?w=1600&q=85&auto=format&fit=crop",
  "technical-drawings": "https://images.unsplash.com/photo-1503387762-592deb58ef4e?w=1600&q=85&auto=format&fit=crop",
  "3d-visualisation":   "https://images.unsplash.com/photo-1620714223084-8fcacc2dbe8d?w=1600&q=85&auto=format&fit=crop",
};
const DEFAULT_HERO_QUOTES: Record<string, string> = {
  "audio":              "Sound That Moves Crowds",
  "lighting":           "Light That Tells Your Story",
  "led-video":          "Every Pixel, Perfect",
  "projection-mapping": "Where Surfaces Come Alive",
  "sfx-laser":          "The Moment They'll Never Forget",
  "rigging-staging":    "Built to Hold the Spectacular",
  "technical-drawings": "Precision Before Performance",
  "3d-visualisation":   "See It Before You Build It",
};
const DEFAULT_SERVICE_HERO = "https://images.unsplash.com/photo-1492684223066-81342ee5ff30?w=1920&q=88&auto=format&fit=crop";

// Rigging and Stage Design were merged into a single "Rigging & Staging" service.
// Keep the old URLs alive so existing links and search results don't 404.
const LEGACY_SLUG_REDIRECTS: Record<string, string> = {
  "rigging": "rigging-staging",
  "stage-design": "rigging-staging",
};

const ServiceDetail: React.FC = () => {
  useReveal();
  const { slug } = useParams();
  const [svc, setSvc] = useState<Resolved | null | undefined>(undefined); // undefined=loading
  const [heroUrl, setHeroUrl] = useState<string>("");
  const [idx, setIdx] = useState(0);
  const idxRef = useRef(0);

  // Compute the top-of-page slideshow images:
  //   1. carousel_images (if present)
  //   2. otherwise hero_image_url from CMS
  //   3. otherwise the per-slug default Unsplash photo
  //   4. final fallback
  const topImages: string[] = (() => {
    if (svc && svc.carousel.length > 0) return svc.carousel;
    if (heroUrl) return [heroUrl];
    const def = slug ? DEFAULT_SERVICE_HERO_BY_SLUG[slug] : undefined;
    return [def || DEFAULT_SERVICE_HERO];
  })();

  useEffect(() => {
    let cancelled = false;
    if (!slug) { setSvc(null); return; }
    dbGet(slug).then(row => {
      if (cancelled) return;
      if (row) { setSvc(fromDb(row)); setHeroUrl(row.hero_image_url || ""); }
      else { setSvc(fromStatic(slug)); setHeroUrl(""); }
    }).catch(() => { setSvc(fromStatic(slug)); setHeroUrl(""); });
    return () => { cancelled = true; };
  }, [slug]);

  useEffect(() => {
    if (!svc || !svc.carousel.length) return;
    idxRef.current = 0; setIdx(0);
    const t = window.setInterval(() => {
      idxRef.current = (idxRef.current + 1) % svc.carousel.length;
      setIdx(idxRef.current);
    }, 4000);
    return () => window.clearInterval(t);
  }, [svc]);

  // Old service URLs must not 404 after the Rigging + Stage Design merge.
  if (slug && LEGACY_SLUG_REDIRECTS[slug]) return <Navigate to={`/services/${LEGACY_SLUG_REDIRECTS[slug]}`} replace />;

  if (svc === undefined) return <main className="iav-page" style={{ minHeight: "100vh" }} />;
  if (svc === null) return <Navigate to="/services" replace />;

  const go = (delta: number) => { const n = svc.carousel.length; if (!n) return; idxRef.current = (idxRef.current + delta + n) % n; setIdx(idxRef.current); };
  const brands2x = [...svc.brands, ...svc.brands];

  return (
    <main className="iav-page" data-testid="service-detail">
      {/* Top slideshow — uses carousel images if present, else hero_image_url or per-slug default. */}
      <section className="iav-sd-top" data-testid="service-hero">
        {topImages.length > 1 ? (
          <div className="iav-sd-carousel" data-testid="sd-carousel">
            {topImages.map((src, i) => (
              <div key={src + i} className={`iav-sd-slide ${i === idx ? "is-active" : ""}`} style={{ backgroundImage: `url(${src})` }} />
            ))}
            <div className="iav-sd-top-overlay" aria-hidden />
            {svc.heroQuote && <div className="iav-sd-quote" data-testid="service-hero-quote">{svc.heroQuote}</div>}
            <button className="iav-sd-arrow is-prev" onClick={() => go(-1)} aria-label="Previous">‹</button>
            <button className="iav-sd-arrow is-next" onClick={() => go(1)} aria-label="Next">›</button>
            <div className="iav-sd-dots">
              {topImages.map((_, i) => (
                <button key={i} className={`iav-sd-dot ${i === idx ? "is-active" : ""}`} onClick={() => { idxRef.current = i; setIdx(i); }} aria-label={`Slide ${i + 1}`} />
              ))}
            </div>
          </div>
        ) : (
          <div className="iav-sd-single" style={{ backgroundImage: `url(${topImages[0]})` }}>
            <div className="iav-sd-top-overlay" aria-hidden />
            {svc.heroQuote && <div className="iav-sd-quote" data-testid="service-hero-quote">{svc.heroQuote}</div>}
          </div>
        )}
      </section>

      <div className="iav-sd-grid">
        <div className="iav-sd-left" data-reveal>
          <span className="iav-tag-line">Service</span>
          <h1>{svc.name}</h1>
          <div className="iav-cta-row">
            <Link to="/quote" className="iav-btn iav-btn-primary">Request This Service</Link>
            <Link to="/services" className="iav-btn iav-btn-outline">All Services</Link>
          </div>
        </div>
        <div className="iav-section-body" data-reveal>
          {svc.isHtml ? (
            <>
              <div className="iav-rich" dangerouslySetInnerHTML={{ __html: svc.paragraph1 }} />
              <div className="iav-rich" dangerouslySetInnerHTML={{ __html: svc.paragraph2 }} />
            </>
          ) : (
            <>
              <p>{svc.paragraph1}</p>
              <p>{svc.paragraph2}</p>
            </>
          )}
        </div>
      </div>

      {svc.brands.length > 0 && (
        <section className="iav-marquee" data-testid="brand-marquee">
          <div className="iav-marquee-h">Equipment We Use</div>
          <div className="iav-marquee-track-wrap">
            <div className="iav-marquee-track">
              {brands2x.map((b, i) => (
                <span key={i} className="iav-marquee-item">
                  {b.logo_url
                    ? <img src={b.logo_url} alt={b.name || "Brand logo"} className="iav-marquee-logo" style={b.height ? { height: `${b.height}px` } : undefined} />
                    : <span className="iav-marquee-text" style={b.font_size ? { fontSize: `${b.font_size}px` } : undefined}>{b.name}</span>}
                </span>
              ))}
            </div>
          </div>
        </section>
      )}
    </main>
  );
};
export default ServiceDetail;
